import { Fragment, useLayoutEffect, useMemo, useRef, useState } from "react"
import { CloseIcon } from "components/ui/icons/close/CloseIcon"
import { focusElement, focusFirstFocusable, getDeepActiveElement } from "../hotkeys/focus"
import { EDITOR_HOTKEYS } from "../hotkeys/hotkeyRegistry"
import type { THotkeyCategory } from "../hotkeys/hotkeyRegistry"
import { groupHotkeysByCategory, isApplePlatform, parseHotkeyLabel } from "./keyboardShortcuts.helpers"
import style from "./KeyboardShortcutsPanel.module.scss"

const { panel, header, tabs, tab, tabActive, close, grid, row, description, keys, key, separator } = style

type Props = {
	onClose: () => void
}

// The keyboard-shortcuts help panel (REQ-359): a bar docked across the bottom of the editor listing
// every shortcut the editor binds, grouped into the design's four tabs.
//
// It renders straight from EDITOR_HOTKEYS, which is the whole point of the requirement — "the list
// must reflect the hotkeys actually supported at the time of viewing". There is no second list to
// keep in step: a shortcut added to the registry shows up here, and a key the editor does NOT bind
// (Alt+2, reserved for the middle nav menu) cannot appear.
export const KeyboardShortcutsPanel = ({ onClose }: Props) => {
	const panelRef = useRef<HTMLDivElement>(null)
	// Read once per mount: the platform cannot change under an open panel, and the modifier glyphs
	// differ on a Mac (⌘ / ⌥ / ⇧ rather than Ctrl / Alt / Shift).
	const isApple = useMemo(() => isApplePlatform(), [])
	const groups = useMemo(() => groupHotkeysByCategory(EDITOR_HOTKEYS), [])
	const [selectedCategory, setSelectedCategory] = useState<THotkeyCategory | null>(null)
	// Falls back to the first tab, so the panel opens on it and can never end up with nothing
	// selected if the chosen category's shortcuts are removed from the registry.
	const activeGroup = groups.find((group) => group.id === selectedCategory) ?? groups[0]

	// The panel opens with focus wherever the user left it — and if that is a sidebar, ITS Tab trap
	// still owns the key (hotkeys/useEditorHotkeys → cycleSidebarFocus only acts when the focused
	// element is inside a sidebar root). Tab would then keep cycling rows behind the open panel and
	// never reach these tabs or the close button. So take focus on mount, landing on the first tab with
	// its keyboard ring so it is visible where the keyboard now is; falling back to the panel itself if
	// nothing focusable is measurable.
	//
	// On close, hand focus back to whatever had it — but only if focus is still inside the panel. If
	// the user clicked elsewhere meanwhile they have already moved on, and yanking it back would be the
	// bug in reverse.
	//
	// useLayoutEffect, NOT useEffect, and that is the whole trick: React detaches the panel's DOM
	// before it flushes PASSIVE unmount effects, so by the time a useEffect cleanup ran, focus had
	// already fallen to <body> and the "still inside the panel" check could never be true — the
	// hand-back silently never happened. Layout cleanups run during the commit, before removal, so the
	// check still sees where focus really is.
	useLayoutEffect(() => {
		const returnTo = getDeepActiveElement()
		const panel = panelRef.current
		if (panel && !focusFirstFocusable(panel)) focusElement(panel)
		return () => {
			if (!panel || !returnTo?.isConnected) return
			const active = getDeepActiveElement()
			if (!active || !panel.contains(active)) return
			focusElement(returnTo)
		}
	}, [])

	if (!activeGroup) return null

	return (
		<div className={panel} role="dialog" aria-label="Keyboard shortcuts" ref={panelRef} tabIndex={-1}>
			<div className={header}>
				<div className={tabs} role="tablist" aria-label="Shortcut categories">
					{groups.map((group) => {
						const isActive = group.id === activeGroup.id
						return (
							<button
								key={group.id}
								type="button"
								role="tab"
								aria-selected={isActive}
								className={`${tab}${isActive ? ` ${tabActive}` : ""}`}
								onClick={() => setSelectedCategory(group.id)}
							>
								{group.label}
							</button>
						)
					})}
				</div>
				<button type="button" className={close} onClick={onClose} aria-label="Close keyboard shortcuts">
					<CloseIcon size={14} color="currentColor" />
				</button>
			</div>

			<div className={grid} role="tabpanel" aria-label={activeGroup.label}>
				{activeGroup.hotkeys.map((hotkey) => (
					<div className={row} key={hotkey.id}>
						{/* title: the row gives the description one line, so an entry longer than the
						    registry's current copy stays readable on hover instead of just ellipsing. */}
						<span className={description} title={hotkey.description}>
							{hotkey.description}
						</span>
						<span className={keys}>
							{parseHotkeyLabel(hotkey.label, isApple).map((alternative, index) => (
								<Fragment key={alternative.join("+")}>
									{index > 0 ? <span className={separator}>/</span> : null}
									{alternative.map((keyLabel, position) => (
										<kbd className={key} key={`${keyLabel}-${position}`}>
											{keyLabel}
										</kbd>
									))}
								</Fragment>
							))}
						</span>
					</div>
				))}
			</div>
		</div>
	)
}
